import { useEffect, useRef, useState } from "react";
import DemoMenu from "../pages/DemoMenu";
import DemoOwner from "../pages/DemoOwner";
import DemoSwitcherBar, { DEMO_EXIT_MS, demoEnterClass, demoExitClass } from "./DemoSwitcherBar";

/** Demo-only shell: mounts BOTH demo experiences in parallel on init and keeps
 *  them in the DOM permanently. Switching only toggles visibility + slide, so
 *  there is no remount, no refetch and no loading state.
 *  `subPage` lets demo sub-routes (cart/checkout/order) act as the customer view
 *  while the owner view stays pre-rendered next to it. */
export default function DemoShell({ initial = "customer", subPage = null }) {
  const [active, setActive] = useState(initial);
  const [leaving, setLeaving] = useState(null); // { experience, dir }
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const go = (next) => {
    if (next === active || leaving) return;
    const dir = next === "owner" ? "left" : "right"; // where the current view goes
    setLeaving({ experience: active, dir });
    setActive(next);
    window.scrollTo(0, 0);
    timer.current = setTimeout(() => setLeaving(null), DEMO_EXIT_MS + 90);
  };

  const pane = (experience, node) => {
    const isActive = experience === active;
    const isLeaving = leaving?.experience === experience;
    // Always rendered: hidden panes keep their state, data and scroll position.
    if (!isActive && !isLeaving) return <div className="demo-pane-hidden">{node}</div>;
    if (isLeaving) return <div className={`demo-pane-leaving ${demoExitClass(leaving.dir)}`}>{node}</div>;
    return <div className={demoEnterClass(leaving?.dir)}>{node}</div>;
  };

  return (
    <div className="demo-shell">
      {pane("customer", subPage || <DemoMenu />)}
      {pane("owner", <DemoOwner />)}

      <DemoSwitcherBar active={active} onSelect={go} />
    </div>
  );
}

/** Demo sub-routes (checkout / order confirmation): the sub-page becomes the
 *  customer pane inside the shell, so the owner view stays mounted next to it
 *  and switching is instant, in place, with no URL change. */
export function DemoPageShell({ children }) {
  return (
    <div className="demo-page-shell">
      <DemoShell initial="customer" subPage={children} />
    </div>
  );
}
